import React from 'react';
import { Bug, Rat, Zap, Building2, Home, Target, Bird } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { services } from '../data/mock';

const iconMap = {
  Bug: Bug,
  Mouse: Rat,
  Zap: Zap,
  Building2: Building2,
  Home: Home,
  Target: Target,
  Bird: Bird,
};

const ServiceCard = ({ service }) => {
  const IconComponent = iconMap[service.icon] || Bug;
  return (
    <Card
      data-testid={`service-card-${service.id}`}
      className={`group bg-white border-neutral-200 hover:border-brand/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden flex flex-col h-full ${
        service.featured ? 'ring-2 ring-brand/40' : ''
      }`}
    >
      {/* Service Image */}
      <div className="relative h-48 overflow-hidden bg-neutral-100">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        {service.featured && (
          <Badge className="absolute top-3 left-3 bg-brand text-white font-semibold shadow-md">
            Our Specialty
          </Badge>
        )}
        {/* Icon badge */}
        <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center shadow-md">
          <IconComponent className="w-5 h-5 text-brand" />
        </div>
      </div>

      <CardContent className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-semibold text-neutral-900 mb-3">
          {service.title}
        </h3>
        <p className="text-neutral-600 leading-relaxed text-sm">
          {service.description}
        </p>
      </CardContent>
    </Card>
  );
};

const ServicesSection = () => {
  const firstSix = services.slice(0, 6);
  const rest = services.slice(6);

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-brand font-semibold text-sm tracking-wider uppercase mb-4 block">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">
            Comprehensive Pest Control Solutions
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            From bed bug specialists to complete pest management, we protect homes
            and businesses across Lincolnshire with proven, eco-friendly methods.
          </p>
        </div>

        {/* Services Grid - First 6 in 3 columns */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {firstSix.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        {/* 7th service - centered */}
        {rest.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="hidden lg:block" />
            {rest.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
            <div className="hidden lg:block" />
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
